<template>
  <div
    class="rounded-lg pa-5"
    :style="{
      display: 'grid',
      backgroundColor: '#F9FAFB',
      gap: '12px',
      gridTemplateColumns: isMobileOrTablet ? '1fr' : '1fr auto 1fr',
    }"
  >
    <div
      class="d-flex flex-column"
      :style="{ gap: '6px', minWidth: 0, overflow: 'hidden' }"
    >
      <div>
        <template v-if="isEventGuest">
          <div class="d-flex py-2" :style="{ width: 'fit-content' }">
            <v-icon left small color="green"> mdi-email-plus </v-icon>
            <p class="ma-0 font-weight-bold">Evento por Invitación</p>
          </div>
          <v-divider />
        </template>

        <event-details-invitations
          :event="event"
          :suggested-guests="suggestedGuests"
          :disabled="disabled"
          :read-only="readOnly"
          :loading="isLoading"
          @createInvitation="onCreateInvitation"
          @resendInvitation="onResendInvitation"
          @revokeInvitation="onRevokeInvitation"
        />

        <v-divider />

        <p
          v-for="{ id, label, value } in eventDetails"
          :key="id"
          class="ma-0 text-left font-weight-bold"
        >
          {{ label }}: <span class="font-weight-regular">{{ value }}</span>
        </p>
      </div>

      <div v-if="eventActivities?.length">
        <v-divider />
        <p class="ma-0 text-left font-weight-bold">Actividades Planeadas</p>
        <div
          v-for="activity in eventActivities"
          :key="activity.id"
          class="text-left"
        >
          <p class="ma-0">{{ activity.label }}</p>
          <p
            v-for="{ value, label } in activity.subactivities"
            :key="value"
            class="ma-0 pl-3"
          >
            - {{ label }}
          </p>
        </div>
      </div>
    </div>

    <v-divider v-if="!isMobileOrTablet" vertical class="mx-4" />

    <div
      class="d-flex flex-column order-md-1"
      :style="{ gap: '12px', minWidth: 0 }"
    >
      <!-- DATES INFO -->
      <div class="w-100 d-flex flex-column" :style="{ gap: '12px' }">
        <!-- ORIGINAL EVENT DATE -->
        <div class="d-flex w-100 justify-space-between align-start">
          <div :style="{ flex: 1 }">
            <p class="ma-0 text-left font-weight-bold">
              Fecha:
              <span class="font-weight-regular">{{ startDateLabel }}</span>
            </p>
            <p class="ma-0 text-left font-weight-bold">
              Horario:
              <span class="font-weight-regular">{{ timeLabel }}</span>
            </p>
            <p class="ma-0 text-left font-weight-bold">
              Estatus:
              <span
                class="font-weight-regular"
                :style="{ color: getEventStatusColor(event, true) }"
              >
                {{
                  event.execution
                    ? event.execution.confirmationStatus.label
                    : event.planningClassification?.label
                }}
              </span>
            </p>
          </div>

          <v-tooltip v-if="!disabled && !readOnly" bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                color="#AF64FD"
                :disabled="isLoading"
                icon
                v-bind="attrs"
                v-on="on"
                @click="
                  () => {
                    if (readOnly) return;

                    isEventRecurrenceFormOpen = false;
                    isEventRescheduleFormOpen = !isEventRescheduleFormOpen;
                  }
                "
              >
                <v-icon>
                  {{
                    isEventRescheduleFormOpen
                      ? 'mdi-clock-alert'
                      : 'mdi-clock-alert-outline'
                  }}
                </v-icon>
              </v-btn>
            </template>
            <span>Reprogramar Evento</span>
          </v-tooltip>
        </div>

        <v-slide-x-reverse-transition>
          <event-details-reschedule-form
            v-if="isEventRescheduleFormOpen"
            :available-locations="availableLocations"
            :disabled="disabled"
            :event="event"
            :loading="isLoading"
            @onReschedule="onReschedule"
          />
        </v-slide-x-reverse-transition>
      </div>

      <v-divider v-if="isEventRescheduleFormOpen" />

      <!-- RECURRENCE INFO -->
      <div class="w-100 d-flex flex-column" :style="{ gap: '12px' }">
        <!-- ORIGINAL EVENT RECURRENCE -->
        <div class="d-flex w-100 justify-space-between align-start">
          <div :style="{ flex: 1 }">
            <p class="ma-0 text-left font-weight-bold">Recurrencia</p>
            <p
              v-if="isReprogrammedException"
              class="ma-0 text-left text--secondary"
            >
              Este evento pertenecía a una serie y fue reagendado. Este tipo de
              eventos no permiten generar recurrencia
            </p>
            <p v-else class="ma-0 text-left">
              {{ getRecurrenceLabel(event.recurrence) }}
            </p>
          </div>

          <v-tooltip
            v-if="!disabled && !readOnly && canUpdateRecurrence"
            bottom
          >
            <template #activator="{ on, attrs }">
              <v-btn
                :disabled="isLoading"
                :elevation="0"
                icon
                v-bind="attrs"
                v-on="on"
                @click="
                  () => {
                    if (readOnly) return;

                    isEventRescheduleFormOpen = false;
                    isEventRecurrenceFormOpen = !isEventRecurrenceFormOpen;
                  }
                "
              >
                <v-icon>
                  {{
                    isEventRecurrenceFormOpen
                      ? 'mdi-clock-edit'
                      : 'mdi-clock-edit-outline'
                  }}
                </v-icon>
              </v-btn>
            </template>
            <span>Modificar Recurrencia</span>
          </v-tooltip>
        </div>

        <v-slide-x-reverse-transition>
          <event-details-recurrence-form
            v-if="isEventRecurrenceFormOpen"
            :available-locations="availableLocations"
            :disabled="disabled"
            :event="event"
            :loading="isLoading"
            @onUpdateRecurrence="onUpdateRecurrence"
          />
        </v-slide-x-reverse-transition>
      </div>
    </div>
  </div>
</template>

<script>
  import EventDetailsRescheduleForm from './EventDetailsRescheduleForm.vue';
  import EventDetailsRecurrenceForm from './EventDetailsRecurrenceForm.vue';
  import EventDetailsInvitations from './EventDetailsInvitations.vue';
  import { getRecurrenceLabel } from '@/utilities/schedule/recurrence.js';
  import {
    getEventStatusColor,
    isWithinConfirmationWindow,
  } from '@/utilities/schedule/event.js';
  import { roleLabels } from '@/core/constants/roleLabels.js';
  import { Roles } from '@/core/constants/roleConstants.js';
  import {
    EventAction,
    EventKind,
    EventType,
  } from '@/core/constants/schedule/event';
  import useGeolocation from '@/mixins/useGeolocation.js';
  import DateFormats from '@/core/constants/DateFormats';
  import { eventBus } from '@/main.js';
  import screen from '@/mixins/screen';
  import {
    capitalizeString,
    getNameInitials,
    getColorHsl,
  } from '@/utilities/helpers.js';

  import { mapGetters, mapActions } from 'vuex';
  import moment from 'moment';

  export default {
    name: 'EventDetails',
    components: {
      EventDetailsRescheduleForm,
      EventDetailsRecurrenceForm,
      EventDetailsInvitations,
    },
    mixins: [screen, useGeolocation],
    props: {
      event: {
        type: Object,
        required: true,
      },
      customerShippingAddresses: {
        type: Array,
        default: () => [],
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      readOnly: {
        type: Boolean,
        default: false,
      },
    },
    data: () => {
      return {
        isLoading: false,
        isEventRescheduleFormOpen: false,
        isEventRecurrenceFormOpen: false,
      };
    },
    computed: {
      ...mapGetters('auth', ['user']),
      ...mapGetters('schedule', ['relatedUsers', 'isLoadingRelatedUsers']),
      canUpdateRecurrence() {
        return this.event.kind !== EventKind.OCCURRENCE_MATERIALIZED;
      },
      isReprogrammedException() {
        return (
          this.event.kind === EventKind.OCCURRENCE_MATERIALIZED &&
          !this.event.execution
        );
      },
      startDateLabel() {
        return moment(this.event.startDate).format(
          'dddd DD [de] MMMM [del] YYYY'
        );
      },
      timeLabel() {
        const startTime = moment(this.event.startDate).format(
          DateFormats.DISPLAY_TIME_AMPM
        );
        const endTime = moment(this.event.endDate).format(
          DateFormats.DISPLAY_TIME_AMPM
        );

        return `${startTime} a ${endTime}`;
      },
      eventDetails() {
        const event = this.event;

        return [
          { id: 'type', label: 'Tipo', value: event.type.label },
          {
            id: 'customer',
            label: 'Cliente',
            value: event.customer
              ? `${event.customer.code} ${event.customer.name}`
              : null,
          },
          {
            id: 'prospect',
            label: 'Prospecto',
            value:
              event.type?.value === EventType.PROSPECTION
                ? event.prospect?.businessName
                : null,
          },
          {
            id: 'location',
            label: 'Ubicación',
            value: event.location,
          },
          {
            id: 'comment',
            label: 'Notas',
            value: event.comment,
          },
        ].filter((d) => d.value);
      },
      eventActivities() {
        const activities = this.event.activities;
        return activities?.map((a) => ({
          id: a.activity.value,
          label: a.activity.label,
          subactivities: a.subactivities,
        }));
      },
      isProspectionEvent() {
        return this.event.type.value === EventType.PROSPECTION;
      },
      isCustomerVisitEvent() {
        return this.event.type.value === EventType.CUSTOMER_VISIT;
      },
      isEventGuest() {
        return this.event?.participants?.some(
          (p) => p.user.id === this.user?.id && p.type.value === 'guest'
        );
      },
      suggestedGuests() {
        const roleOrder = [
          Roles.DIRECTOR,
          Roles.SUB_DIRECTOR,
          Roles.MANAGER,
          Roles.SUB_MANAGER,
          Roles.REGIONAL_MANAGER,
          Roles.SELLER,
        ];

        const selectedEmails = new Set(
          this.event.invitations?.map((i) => i.email) ?? []
        );

        return roleOrder.reduce((acc, role) => {
          const users = this.relatedUsers
            .filter((u) => u.role === role && !selectedEmails.has(u.email))
            .map((u) => ({
              ...u,
              name: capitalizeString(u.name),
              avatar: getNameInitials(u.name),
              avatarColor: getColorHsl(u.id),
              disabled: !u.email,
            }));

          if (users.length) {
            acc.push({ header: roleLabels[role] });
            acc.push(...users);
          }
          return acc;
        }, []);
      },
      prospectLocations() {
        return this.event.prospect?.address
          ? [this.event.prospect.address]
          : [];
      },
      customerLocations() {
        return this.customerShippingAddresses.map((a) => {
          const address = `${a.KUNNR} ${a.STRAS} ${a.ORT01} ${a.PSTLZ}`;
          return { text: address, value: address };
        });
      },
      availableLocations() {
        if (this.isProspectionEvent) return this.prospectLocations;
        if (this.isCustomerVisitEvent) return this.customerLocations;
        return [];
      },
    },
    watch: {
      event() {
        this.closeForms();
      },
    },
    methods: {
      ...mapActions('schedule', [
        'updateUserEvent',
        'getUserRelatedUsers',
        'createEventInvitation',
        'revokeEventInvitation',
      ]),
      getEventStatusColor,
      getRecurrenceLabel,
      closeForms() {
        this.isEventRescheduleFormOpen = false;
        this.isEventRecurrenceFormOpen = false;
      },
      formatTime(date) {
        if (!date) return '';
        return moment(date).format(DateFormats.DISPLAY_TIME_AMPM_LOWER);
      },
      formatDate(date) {
        if (!date) return '';
        return moment(date).format('ddd D [de] MMM [del] YYYY');
      },
      formatDateTime(date) {
        if (!date) return '';
        return moment(date).format('ddd D [de] MMM [del] YYYY [a las] hh:mm a');
      },
      async onReschedule(rescheduleParams) {
        if (this.readOnly) return;
        this.isLoading = true;

        const geolocation = await this.getGeolocationPosition();

        const eventPayload = {
          startDate: moment(rescheduleParams.startDate).format(
            DateFormats.ISO_DATETIME
          ),
          endTime: moment(rescheduleParams.endDate).format(
            DateFormats.ISO_TIME
          ),
          location: rescheduleParams.location,
          occurrenceId: this.event.occurrenceId,
        };

        const { error, data } = await this.updateUserEvent({
          userId: this.user?.id,
          eventId: this.event.id,
          event: eventPayload,
          action: {
            userId: this.user?.id,
            type: EventAction.RESCHEDULE,
            geolocation,
          },
        });

        if (error) {
          this.isLoading = false;
          this.$toast.error(error);
          return;
        }

        if (!isWithinConfirmationWindow(data)) {
          eventBus.$emit('scheduleRemoveEventConfirmationStep', data.id);
        }

        this.$toast.success('Evento actualizado con éxito');
        this.isLoading = false;
      },
      async onUpdateRecurrence(recurrenceParams) {
        if (this.readOnly) return;
        this.isLoading = true;

        const geolocation = await this.getGeolocationPosition();
        const recurrencePayload = !recurrenceParams.frequency
          ? {
              frequency: this.event.recurrence.frequency?.value,
              interval: this.event.recurrence.interval,
              until: moment(this.event.startDate).format(
                DateFormats.ISO_DATETIME
              ),
              repeatTimes: null,
            }
          : { ...recurrenceParams };

        const eventPayload = {
          location: recurrenceParams.location,
          ...recurrencePayload,
        };

        const { error, data } = await this.updateUserEvent({
          userId: this.user?.id,
          eventId: this.event.id,
          event: eventPayload,
          action: {
            userId: this.user?.id,
            type: EventAction.UPDATE_RECURRENCE,
            geolocation,
          },
        });

        if (error) {
          this.$toast.error(error);
          this.isLoading = false;
          return;
        }

        if (!isWithinConfirmationWindow(data)) {
          eventBus.$emit('scheduleRemoveEventConfirmationStep', data.id);
        }

        this.$emit('update', data);
        this.$toast.success('Evento actualizado con éxito');
        this.isLoading = false;
      },
      async onCreateInvitation(guests) {
        if (this.readOnly) return;

        this.isLoading = true;

        try {
          const results = await Promise.all(
            guests.map((g) =>
              this.createEventInvitation({
                userId: this.user.id,
                eventId: this.event.id,
                email: g.email,
                guestId: g.id,
              })
            )
          );

          const failed = results.find((r) => r?.error);
          if (failed) {
            this.$toast.error(failed.error);
            return;
          }

          this.$toast.success('Invitaciones enviadas con éxito');
        } finally {
          this.isLoading = false;
        }
      },
      async onResendInvitation({ guest }) {
        if (this.readOnly) return;

        this.isLoading = true;
        try {
          const { message, error } = await this.createEventInvitation({
            userId: this.user.id,
            eventId: this.event.id,
            email: guest.email,
            guestId: guest.id,
          });

          if (error) {
            this.$toast.error(error);
            return;
          }

          this.$toast.success(message ?? 'Invitación reenviada con éxito');
        } finally {
          this.isLoading = false;
        }
      },
      async onRevokeInvitation(invitationId) {
        if (this.readOnly) return;

        this.isLoading = true;
        try {
          const { message, error } = await this.revokeEventInvitation({
            eventId: this.event.id,
            invitationId,
          });

          if (error) {
            this.$toast.error(error);
            return;
          }

          this.$toast.success(message ?? 'Invitación eliminada con éxito');
        } finally {
          this.isLoading = false;
        }
      },
    },
  };
</script>

<style scoped>
  .uppercase {
    text-transform: uppercase;
  }
</style>
